import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, FormControlLabel, TextField, Alert, LinearProgress, Typography } from '@mui/material';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import api from '../../util/axiosInstance';
import './documentAgreements.css';
import { contractFileError } from './contractValidation';

GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
const disclaimer = 'I have read all terms and conditions and accept.';
const errorText = e => e.response?.data?.message || e.message || 'Unable to complete document agreement';
export async function fetchAgreementPdf(url) {
  const response = await api.get(url, { responseType: 'arraybuffer' });
  return new Uint8Array(response.data);
}
export function saveAgreementPdf(bytes, name = 'contract.pdf') {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function PdfReader({ bytes, onBottom = () => {} }) {
  const host = useRef(null), scroll = useRef(null);
  const [error, setError] = useState('');
  const bottom = useRef(onBottom); bottom.current = onBottom;
  useEffect(() => {
    let cancelled = false;
    setError('');
    const task = getDocument({ data: bytes.slice(), isEvalSupported: false });
    const target = host.current; target.replaceChildren(); target.dataset.ready = 'false';
    (async () => {
      const doc = await task.promise;
      for (let i = 1; i <= doc.numPages && !cancelled; i++) {
        const page = await doc.getPage(i);
        const original = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: Math.min(1.5, Math.max(250, target.clientWidth - 12) / original.width) });
        const canvas = document.createElement('canvas'); canvas.width = viewport.width; canvas.height = viewport.height;
        canvas.style.maxWidth = '100%'; canvas.style.display = 'block'; canvas.style.margin = '8px auto';
        canvas.setAttribute('aria-label', `Document page ${i}`);
        if (cancelled) break;
        target.appendChild(canvas);
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      }
      if (!cancelled && scroll.current && scroll.current.scrollHeight <= scroll.current.clientHeight + 2) bottom.current();
      if (!cancelled) target.dataset.ready = 'true';
    })().catch(e => { if (!cancelled) setError(errorText(e)); });
    return () => { cancelled = true; task.destroy(); };
  }, [bytes]);
  return <>{error && <Alert severity="error">{error}</Alert>}<div className="agreement-pdf-scroll" ref={scroll} onScroll={e => {
    const el = e.currentTarget;
    if (host.current?.dataset.ready === 'true' && el.scrollTop + el.clientHeight >= el.scrollHeight - 4) bottom.current();
  }}><div ref={host} /></div></>;
}

export default function DocumentAgreementModal({ selection, initial, onClose, beforeCheckout = false }) {
  const [vendors, setVendors] = useState(initial.vendors), [selected, setSelected] = useState([]);
  const [viewer, setViewer] = useState(null), [bytes, setBytes] = useState(null);
  const [consent, setConsent] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [signing, setSigning] = useState(null), [name, setName] = useState(''), [bulkConsent, setBulkConsent] = useState(false);
  const [saveName, setSaveName] = useState(false);
  const reviewing = useRef(false), upload = useRef(null);
  const complete = vendors.every(v => v.documents.every(d => ['Accepted', 'Signed', 'Uploaded'].includes(d.status)));
  const refresh = async () => { const { data } = await api.post('/document-agreements/requirements', selection); setVendors(data.vendors); setSelected([]); return data; };
  const run = async fn => { setBusy(true); setError(''); try { await fn(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); } };
  const open = doc => run(async () => { setBytes(null); setConsent(false); setViewer(doc); setBytes(await fetchAgreementPdf(doc.originalUrl)); });
  const review = async method => {
    if (!viewer || viewer.reviewed || reviewing.current) return;
    reviewing.current = true;
    const id = viewer.id;
    try { await api.post(`/document-agreements/${id}/review`, { method }); setViewer(v => v?.id === id ? { ...v, reviewed: true } : v); }
    catch (e) { setError(errorText(e)); } finally { reviewing.current = false; }
  };
  const finish = async (ids, method, fullName) => {
    const url = ids.length === 1 ? `/document-agreements/${ids[0]}/${method}` : '/document-agreements/bulk-action';
    const { data } = await api.post(url, { ids, method, fullName, disclaimerAccepted: true });
    // Apply server-confirmed results immediately, then synchronize the list.
    setVendors(vs => vs.map(v => ({ ...v, documents: v.documents.map(d => data.documents.find(next => next.id === d.id) || d) })));
    setViewer(null); setSigning(null); await refresh();
  };
  const beginSigning = (ids, method) => run(async () => {
    setName(''); setBulkConsent(false); setSaveName(false);
    if (method === 'digital-sign') {
      const { data } = await api.get('/document-agreements/signature');
      setName(data.name || '');
    }
    setSigning({ ids, method });
  });
  const beginBulk = method => beginSigning(selected, method);
  return <Dialog open fullWidth maxWidth="md" onClose={() => { if (!busy) onClose(false); }}>
    <DialogTitle>{viewer ? viewer.fileName : 'Document agreements'}</DialogTitle>
    {busy && <LinearProgress />}
    <DialogContent>
      {error && <Alert severity="error">{error}</Alert>}
      {viewer ? <>
        <Button disabled={!bytes || busy} onClick={async () => {
          saveAgreementPdf(bytes, viewer.fileName); await review('downloaded');
        }}>Download PDF</Button>
        {bytes && <PdfReader bytes={bytes} onBottom={() => review('scrolled')} />}
        {!viewer.reviewed && <Typography>Scroll to the bottom or download the PDF to continue.</Typography>}
        {viewer.status === 'Pending' ? <>
          <FormControlLabel control={<Checkbox checked={consent} disabled={!viewer.reviewed || busy} onChange={e => setConsent(e.target.checked)} />} label={disclaimer} />
          <div className="agreement-actions">
            <Button variant="contained" disabled={!viewer.reviewed || !consent || busy} onClick={() => run(() => finish([viewer.id], 'accept'))}>Accept</Button>
            <Button variant="outlined" disabled={!viewer.reviewed || !consent || busy} onClick={() => beginSigning([viewer.id], 'digital-sign')}>Digital Sign</Button>
            <Button variant="outlined" disabled={!viewer.reviewed || !consent || busy} onClick={() => upload.current.click()}>Upload Signed Copy</Button>
            <input type="file" accept=".pdf,application/pdf" hidden ref={upload} onChange={e => {
              const file = e.target.files?.[0]; e.target.value = ''; if (!file) return;
              const validationError = contractFileError(file);
              if (validationError) { setError(validationError); return; }
              run(async () => { const form = new FormData(); form.append('file', file); form.append('disclaimerAccepted', 'true');
                await api.post(`/document-agreements/${viewer.id}/upload`, form); setViewer(null); await refresh(); });
            }} />
          </div>
        </> : <Alert severity="success">Completed: {viewer.status}</Alert>}
      </> : <>
        <Typography>Review each provider document before payment. Only reviewed documents can be selected for bulk actions.</Typography>
        {vendors.map(v => <section key={v.providerId} className="agreement-vendor"><h3>{v.providerName}</h3>{v.documents.map(d => <div key={d.id} className="agreement-row">
          {d.status !== 'Pending' ? <span className="agreement-complete" aria-label="Completed">&#10003;</span> : <Checkbox checked={selected.includes(d.id)} disabled={!d.reviewed || busy || (!selected.includes(d.id) && selected.length >= 20)} onChange={e => setSelected(s => e.target.checked ? [...s, d.id] : s.filter(id => id !== d.id))} />}
          <div className="agreement-file"><strong title={d.fileName}>{d.fileName}</strong><span>{d.status}</span></div>
          <Button disabled={busy} onClick={() => open(d)}>View / Action</Button>
        </div>)}</section>)}
        {!!selected.length && <div className="agreement-actions"><Typography>{selected.length} selected</Typography><Button disabled={busy} onClick={() => beginBulk('accept')}>Bulk Accept</Button><Button disabled={busy} onClick={() => beginBulk('digital-sign')}>Bulk Digital Sign</Button></div>}
      </>}
    </DialogContent>
    <DialogActions>
      {viewer ? <Button disabled={busy} onClick={() => run(async () => { setViewer(null); await refresh(); })}>Back to documents</Button> : <>
        <Button disabled={busy} onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="contained" disabled={!complete || busy} onClick={() => run(async () => {
          try {
            if (beforeCheckout) {
              const latest = await refresh();
              if (!latest.complete) return;
            } else { await api.post('/document-agreements/validate', selection); }
            onClose(true);
          }
          catch (e) { await refresh(); throw e; }
        })}>{beforeCheckout ? 'Done reviewing' : 'Proceed to Payment'}</Button>
      </>}
    </DialogActions>
    <Dialog open={!!signing} onClose={() => { if (!busy) setSigning(null); }} fullWidth maxWidth="sm">
      <DialogTitle>{signing?.method === 'digital-sign' ? 'Digital signature' : 'Accept selected documents'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {signing?.method === 'digital-sign' && <><TextField fullWidth label="Full name" value={name} inputProps={{ maxLength: 120 }} onChange={e => setName(e.target.value)} /><div className="agreement-signature">{name}</div><FormControlLabel control={<Checkbox checked={saveName} disabled={busy} onChange={e => setSaveName(e.target.checked)} />} label="Save this name for future signatures" /></>}
        <FormControlLabel control={<Checkbox checked={bulkConsent} onChange={e => setBulkConsent(e.target.checked)} />} label={disclaimer} />
      </DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setSigning(null)}>Cancel</Button><Button disabled={busy || !bulkConsent || (signing?.method === 'digital-sign' && name.trim().length < 2)} onClick={() => run(async () => {
        if (signing.method === 'digital-sign' && saveName) await api.put('/document-agreements/signature', { name: name.trim() });
        await finish(signing.ids, signing.method, name.trim());
      })}>Confirm</Button></DialogActions>
    </Dialog>
  </Dialog>;
}

export async function requestDocumentAgreements(selection, { beforeCheckout = false, showCompleted = false } = {}) {
  const { data } = await api.post('/document-agreements/requirements', selection);
  if (data.complete && !showCompleted) return true;
  return new Promise(resolve => {
    const node = document.createElement('div'); document.body.appendChild(node);
    const root = createRoot(node);
    root.render(<DocumentAgreementModal selection={selection} initial={data} beforeCheckout={beforeCheckout} onClose={result => {
      root.unmount(); node.remove(); resolve(result);
    }} />);
  });
}
